// Les dues formes dels models d'estocs i fluxos, i com es passa de l'una a l'altra.
//
// Sense cap import, com `mapesBuclesCausals.js`, perquè es pugui provar sol. La prova fa la volta
// sencera —client → API → PostgreSQL → API → client— i compara el que surt amb el que va entrar.
//
// ── LA DIFERÈNCIA AMB ELS BUCLES CAUSALS ─────────────────────────────────────────────────────
//
// Allà hi ha dues taules —variables i relacions— i les relacions apunten a les variables per
// clau forana. Aquí **hi ha una sola taula**, `model_components`, i les canonades i els
// connectors hi van com un component més, amb els extrems dins de `properties`. No és una
// drecera: la taula ja té l'enum amb `flow` i `connector`, i partir-ho en dues hauria volgut dir
// una migració d'esquema per no guanyar res.
//
// El que sí que es comparteix és **el problema dels identificadors**. Mentre es dibuixa són un
// comptador del navegador; en desar, la base assigna UUID. Una canonada desada apunta als
// identificadors vells, o sigui que en tornar cal refer-los amb la correspondència que dona el
// desat. Sense això, després del primer desat cap fletxa trobaria els seus extrems i el dibuix
// es desmuntaria — que és, exactament, el que passava amb els lligams causals abans del
// 26/08/2026.
//
// ── LA POSICIÓ ÉS EL CENTRE ──────────────────────────────────────────────────────────────────
//
// Fins al 26/08/2026 `x`,`y` eren el **cantó** de la figura, i llavors tota la geometria
// d'ancoratge havia d'anar sumant mitges amplades. Ara són el **centre**. Es podia canviar
// perquè a la base no hi havia cap component; el dia que n'hi hagi, això ja no serà gratis.

// Dos tipus no porten nom, i **no és que sigui opcional: és que no en tenen**. Una fletxa
// d'informació no en porta a la notació, i un núvol és el que queda FORA de la frontera del
// model —posar-li nom seria descriure allò de què, per definició, no en sabem res—. La resta de
// peces sí que en volen: un estoc sense nom no es pot fer servir a cap equació.
export const VOL_NOM = (tipus) => tipus !== 'connector' && tipus !== 'cloud';

export const componentCapALAPI = (c) => ({
  clientId: String(c.id),
  type: c.type,
  // L'API demana un nom no buit per a tot el que no és un connector; per als connectors s'envia
  // el que hi hagi, encara que sigui una cadena buida.
  name: c.name || '',
  x: Number(c.x) || 0,
  y: Number(c.y) || 0,
  properties: { ...(c.properties || {}) },
});

export const componentDesDeLAPI = (c) => ({
  id: c.id,
  type: c.componentType || c.component_type || c.type,
  name: c.name || '',
  x: c.position?.x ?? 0,
  y: c.position?.y ?? 0,
  properties: c.properties || {},
});

// Cap a l'API: els extrems de les fletxes es deixen tal com són —encara són identificadors del
// navegador— i el servidor els desa dins de `properties`. La correspondència es refà en tornar.
export function capALAPI(components) {
  return components.map(componentCapALAPI);
}

// De l'API cap al llenç. `correspondencia` és el mapa d'identificador provisional a UUID que
// torna el desat; si no n'hi ha —una lectura normal— els extrems ja són UUID i es deixen estar.
export function desDeLAPI(components, correspondencia = null) {
  const refes = components.map(componentDesDeLAPI);
  if (!correspondencia) return refes;
  const nou = (id) => (id == null ? null : (correspondencia[String(id)] ?? id));
  return refes.map((c) => (c.properties?.from !== undefined || c.properties?.to !== undefined)
    ? { ...c, properties: { ...c.properties, from: nou(c.properties.from), to: nou(c.properties.to) } }
    : c);
}

// La correspondència entre el que s'ha enviat i el que ha tornat. L'API desa i retorna **en el
// mateix ordre**, que és el que permet aparellar-ho; si algun dia deixés de ser així, això s'ha
// de refer amb un `clientId` que viatgi d'anada i tornada.
export function correspondenciaIdentificadors(enviats, tornats) {
  const mapa = {};
  enviats.forEach((c, i) => { if (tornats[i]) mapa[String(c.clientId ?? c.id)] = tornats[i].id; });
  return mapa;
}

// ── LA COMPROVACIÓ D'ABANS D'ENVIAR ──────────────────────────────────────────────────────────
//
// Es fa aquí i no al servidor perquè els extrems viuen dins d'un JSONB i el servidor no els pot
// validar sense conèixer la notació. Una fletxa que apunti a un component que no s'envia deixaria
// un dibuix trencat que només es veuria en tornar-lo a obrir.
export function comprovaAbansDenviar(components) {
  const ids = new Set(components.map((c) => String(c.id)));
  const problemes = [];
  for (const c of components) {
    if (c.type !== 'flow' && c.type !== 'connector') {
      if (VOL_NOM(c.type) && !String(c.name || '').trim()) {
        problemes.push({ id: c.id, clau: 'sf.errSenseNom' });
      }
      continue;
    }
    const { from, to } = c.properties || {};
    if (!from || !to) { problemes.push({ id: c.id, clau: 'sf.errSenseExtrems' }); continue; }
    if (!ids.has(String(from)) || !ids.has(String(to))) {
      problemes.push({ id: c.id, clau: 'sf.errExtremPerdut' });
    }
  }
  return problemes;
}

// El que penja d'una peça: en esborrar-la, se'n van també les fletxes que hi arriben o en surten.
// Sense això quedarien apuntant al no-res i el dibuix s'ompliria de fletxes òrfenes.
export function arrossegaEnEsborrar(components, id) {
  const fora = new Set([String(id)]);
  // Una canonada esborrada s'endú també els connectors que informaven la seva vàlvula.
  let creix = true;
  while (creix) {
    creix = false;
    for (const c of components) {
      if (fora.has(String(c.id))) continue;
      const { from, to } = c.properties || {};
      if ((from && fora.has(String(from))) || (to && fora.has(String(to)))) {
        fora.add(String(c.id)); creix = true;
      }
    }
  }
  return components.filter((c) => !fora.has(String(c.id)));
}

const MAPES = { capALAPI, desDeLAPI, correspondenciaIdentificadors,
                 comprovaAbansDenviar, arrossegaEnEsborrar, VOL_NOM };
export default MAPES;
